/**
 * Centralized toast messages for authentication and profile operations
 * 
 * Uses i18n for internationalization. Technical errors are logged to console,
 * users see friendly localized messages.
 * 
 * @example
 * ```typescript
 * // Login success
 * authToast.loginSuccess();
 * 
 * // Login error
 * authToast.loginError(error);
 * ```
 */

import { toast } from 'sonner';
import { logger } from "@/lib/logger";
import i18n from '@/app/i18n/i18n';

// Helper to get translation
const t = (key: string) => i18n.t(`toast.auth.${key}`, { ns: 'common' });

/**
 * Authentication and profile toast messages
 * All messages are internationalized via i18n
 */
export const authToast = {
  // ========================================
  // LOGIN
  // ========================================
  
  /**
   * Show success toast after successful login
   */
  loginSuccess: () => {
    toast.success(t('loginSuccess'), {
      description: t('loginSuccessDescription'),
    });
  },

  /**
   * Show error toast when login fails
   * @param error - Error object (logged to console, not shown to user)
   */
  loginError: (error?: Error) => {
    logger.error('[Auth] Login failed:', error);
    toast.error(t('loginError'), {
      description: t('loginErrorDescription'),
    });
  },

  // ========================================
  // LOGOUT
  // ========================================
  
  /**
   * Show success toast after successful logout
   */
  logoutSuccess: () => {
    toast.success(t('logoutSuccess'));
  },

  /**
   * Show error toast when logout fails
   * @param error - Error object (logged to console)
   */
  logoutError: (error?: Error) => {
    logger.error('[Auth] Logout failed:', error);
    toast.error(t('logoutError'), {
      description: t('tryAgain'),
    });
  },

  // ========================================
  // PASSWORD RESET
  // ========================================
  
  /**
   * Show success toast when password reset email is sent
   */
  resetEmailSent: () => {
    toast.success(t('resetEmailSent'), {
      description: t('resetEmailSentDescription'),
    });
  },

  /**
   * Show error toast when password reset email fails
   * @param error - Error object (logged to console)
   */
  resetEmailError: (error?: Error) => {
    logger.error('[Auth] Reset email failed:', error);
    toast.error(t('resetEmailError'), {
      description: t('contactSupport'),
    });
  },

  // ========================================
  // PASSWORD UPDATE
  // ========================================
  
  /**
   * Show success toast when password is updated
   */
  passwordUpdated: () => {
    toast.success(t('passwordUpdated'), {
      description: t('passwordUpdatedDescription'),
    });
  },

  /**
   * Show error toast when password update fails
   * @param error - Error object (logged to console)
   */
  passwordUpdateError: (error?: Error) => {
    logger.error('[Auth] Password update failed:', error);
    toast.error(t('passwordUpdateError'), {
      description: t('contactSupport'),
    });
  },

  // ========================================
  // EMAIL UPDATE
  // ========================================
  
  /**
   * Show success toast when email is updated
   */
  emailUpdated: () => {
    toast.success(t('emailUpdated'), {
      description: t('emailUpdatedDescription'),
    });
  },

  /**
   * Show error toast when email update fails
   * @param error - Error object (logged to console)
   */
  emailUpdateError: (error?: Error) => {
    logger.error('[Auth] Email update failed:', error);
    toast.error(t('emailUpdateError'), {
      description: t('contactSupport'),
    });
  },

  // ========================================
  // EMAIL VERIFICATION
  // ========================================
  
  /**
   * Show success toast when verification email is sent
   */
  verificationSent: () => {
    toast.success(t('verificationSent'), {
      description: t('verificationSentDescription'),
    });
  },

  /**
   * Show error toast when verification fails
   * @param error - Error object (logged to console)
   */
  verificationError: (error?: Error) => {
    logger.error('[Auth] Verification failed:', error);
    toast.error(t('verificationError'), {
      description: t('tryAgain'),
    });
  },

  // ========================================
  // ACCOUNT DELETION
  // ========================================
  
  /**
   * Show success toast when account is deleted
   */
  accountDeleted: () => {
    toast.success(t('accountDeleted'), {
      description: t('accountDeletedDescription'),
    });
  },

  /**
   * Show error toast when account deletion fails
   * @param error - Error object (logged to console)
   */
  accountDeleteError: (error?: Error) => {
    logger.error('[Auth] Account deletion failed:', error);
    toast.error(t('accountDeleteError'), {
      description: t('contactSupport'),
    });
  },

  // ========================================
  // PROFILE UPDATE
  // ========================================
  
  /**
   * Show success toast when profile is updated
   */
  profileUpdated: () => {
    toast.success(t('profileUpdated'), {
      description: t('profileUpdatedDescription'),
    });
  },

  /**
   * Show error toast when profile update fails
   * @param error - Error object (logged to console)
   */
  profileUpdateError: (error?: Error) => {
    logger.error('[Auth] Profile update failed:', error);
    toast.error(t('profileUpdateError'), {
      description: t('contactSupport'),
    });
  },

  // ========================================
  // REGISTRATION
  // ========================================
  
  /**
   * Show success toast when registration is successful
   */
  registrationSuccess: () => {
    toast.success(t('registrationSuccess'), {
      description: t('registrationSuccessDescription'),
    });
  },

  /**
   * Show error toast when registration fails
   * @param error - Error object (logged to console)
   */
  registrationError: (error?: Error) => {
    logger.error('[Auth] Registration failed:', error);
    toast.error(t('registrationError'), {
      description: t('contactSupport'),
    });
  },
};
